import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiLoadingStatus } from "enum/api-loading-status.enum";
import { LabelSizes } from "enum/label-sizes.enum";
import { Meta, Query } from "interfaces/api.interface";
import { ILabelJob, ILabelJobHistory } from "models/labelJob/labelJob.model";
import { IShipment } from "models/shipment/shipment.model";
import {
  fetchLabelJobsHistory,
  createLabelJob,
  fetchLabelJobs,
  createS19Labels,
} from "services/api/labels";
import {
  FetchLabelJobsResponseDto,
  FetchLabelJobsHistoryResponseDto,
} from "services/api/labels/dtos/fetch-label-jobs.response.dto";
import { AppThunk, RootState } from "store";
import { trackLabelPrinted } from "utils/gtag";
import { recordLabelsDownloadedThunk } from "store/shipmentsSlice/thunks/recordLabelsDownloadedThunk.thunk";

interface ILabelJobsSlice {
  createLoadingStatus: ApiLoadingStatus;
  s19LoadingStatus: ApiLoadingStatus;
  currentLabelJobUUID: ILabelJob["uuid"] | null;
  loadingStatus: ApiLoadingStatus;
  items: ILabelJob[];
  meta: Meta;
  historyItems: ILabelJobHistory[];
  historyMeta: Meta;
}

export const LabelJobsInitialState: ILabelJobsSlice = {
  createLoadingStatus: ApiLoadingStatus.IDLE,
  s19LoadingStatus: ApiLoadingStatus.IDLE,
  currentLabelJobUUID: null,
  loadingStatus: ApiLoadingStatus.IDLE,
  items: [],
  meta: {
    pageCount: 0,
    offset: 0,
    limit: 0,
    recordCount: 0,
  },
  historyItems: [],
  historyMeta: {
    pageCount: 0,
    offset: 0,
    limit: 0,
    recordCount: 0,
  },
};

export const labelJobsSlice = createSlice({
  name: "labelJobsSlice",
  initialState: LabelJobsInitialState,
  reducers: {
    setData: (state, action: PayloadAction<FetchLabelJobsResponseDto>) => {
      state.items = action.payload.data || LabelJobsInitialState.items;

      state.meta = action.payload.meta || LabelJobsInitialState.meta;

      state.loadingStatus = ApiLoadingStatus.SUCCESS;
    },
    setHistoryData: (
      state,
      action: PayloadAction<FetchLabelJobsHistoryResponseDto>,
    ) => {
      state.historyItems = action.payload.data || [];
      state.historyMeta =
        action.payload.meta || LabelJobsInitialState.historyMeta;
      state.loadingStatus = ApiLoadingStatus.SUCCESS;
    },

    setCreateLoadingStatus: (
      state,
      action: PayloadAction<ILabelJobsSlice["createLoadingStatus"]>,
    ) => {
      state.createLoadingStatus = action.payload;
    },
    setS19LoadingStatus: (
      state,
      action: PayloadAction<ILabelJobsSlice["s19LoadingStatus"]>,
    ) => {
      state.s19LoadingStatus = action.payload;
    },
    setStatus: (
      state,
      action: PayloadAction<ILabelJobsSlice["loadingStatus"]>,
    ) => {
      state.loadingStatus = action.payload;
    },
    setCurrentLabelJobUUID: (
      state,
      action: PayloadAction<ILabelJobsSlice["currentLabelJobUUID"]>,
    ) => {
      state.currentLabelJobUUID = action.payload;
    },
  },
});

export const {
  setData,
  setStatus,
  setCreateLoadingStatus,
  setS19LoadingStatus,
  setCurrentLabelJobUUID,
  setHistoryData,
} = labelJobsSlice.actions;
// Dedicated thunk for label history summary
export const fetchLabelJobsHistoryThunk = (
  query: Query<ILabelJob, { downloaded: "true" | "false" }>,
): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(setStatus(ApiLoadingStatus.LOADING));
    const {
      authSlice: { salesAccountUUID },
      tableSlice: { numberOfRowsSelected },
    } = getState() as RootState;

    if (salesAccountUUID) {
      try {
        // You need to implement fetchLabelJobsHistory in your API service
        const { data, error, meta } = await fetchLabelJobsHistory(
          salesAccountUUID,
          { ...query, limit: numberOfRowsSelected.value },
        );
        if (!error) {
          dispatch(
            setHistoryData({
              meta,
              data,
            }),
          );
        }
      } catch (e) {
        console.log(e);
      }
    }
  };
};

export const fetchLabelJobsThunk = (
  query: Query<ILabelJob, { downloaded: "true" | "false" }>,
): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(setStatus(ApiLoadingStatus.LOADING));

    const {
      authSlice: { salesAccountUUID },
      tableSlice: { numberOfRowsSelected },
    } = getState() as RootState;

    if (salesAccountUUID) {
      try {
        const { data, error, meta } = await fetchLabelJobs(salesAccountUUID, {
          ...query,
          limit: numberOfRowsSelected.value,
        });

        if (!error) {
          dispatch(
            setData({
              meta,
              data,
            }),
          );
        }
      } catch (e) {
        console.log(e);
      }
    }
  };
};

export const createLabelJobThunk = (
  shipmentIds: IShipment["id"][],
): AppThunk => {
  return async (dispatch, getState) => {
    const {
      authSlice: { salesAccountUUID },
      settingsSlice: { settings },
      labelJobsSlice: { createLoadingStatus },
    } = getState() as RootState;

    if (createLoadingStatus === ApiLoadingStatus.LOADING) return;

    await dispatch(setCreateLoadingStatus(ApiLoadingStatus.LOADING));
    dispatch(setCurrentLabelJobUUID(null));

    let labelSize = LabelSizes.x6;
    if (settings?.selectedLabelSize && settings.selectedLabelSize in LabelSizes) {
      labelSize = LabelSizes[settings.selectedLabelSize];
    }
    try {
      const { data, error } = await createLabelJob(salesAccountUUID, {
        shipmentIds,
        labelSize,
        processAsync: true,
      });

      if (!error) {
        // Remembered so the Shipments page can unlock the printed rows once
        // this job finishes (see the lockedShipmentIds effect there).
        dispatch(setCurrentLabelJobUUID(data?.uuid || null));
        dispatch(setCreateLoadingStatus(ApiLoadingStatus.SUCCESS));
        trackLabelPrinted({ method: "bulk", labelCount: shipmentIds.length });
      }

      if (error) {
        dispatch(setCreateLoadingStatus(ApiLoadingStatus.FAILED));
      }
    } catch (e) {
      dispatch(setCreateLoadingStatus(ApiLoadingStatus.FAILED));
    }
  };
};

export const createS19LabelsThunk = (
  shipmentIds: IShipment["id"][],
  labelUrl: string,
): AppThunk => {
  return async (dispatch, getState) => {
    await dispatch(setS19LoadingStatus(ApiLoadingStatus.LOADING));

    const {
      authSlice: { salesAccountUUID },
    } = getState() as RootState;

    try {
      const { data, error } = await createS19Labels(
        salesAccountUUID,
        shipmentIds,
      );

      if (!error && data) {
        dispatch(setS19LoadingStatus(ApiLoadingStatus.SUCCESS));

        // Handle the S19 response data
        if (data.error) {
          console.error("S19 Labels generation failed:", data.error);
          // Could dispatch a toast notification here
        } else if (data.s19Data) {
          console.log("S19 Labels generated successfully");
          // Trigger download of the combined PDF
          const link = document.createElement("a");
          link.href = `data:application/pdf;base64,${data.s19Data}`;
          link.download = labelUrl.replace("/combos/", "combined_");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          dispatch(recordLabelsDownloadedThunk(shipmentIds));
        }
      }

      if (error) {
        dispatch(setS19LoadingStatus(ApiLoadingStatus.FAILED));
      }
    } catch (e) {
      dispatch(setS19LoadingStatus(ApiLoadingStatus.FAILED));
    }
  };
};
